import { ImageSourcePropType } from 'react-native';

/**
 * Content for the three onboarding slides, in order.
 *
 * Headlines and body are single strings and left to wrap on their own. The
 * design breaks each headline over two lines, but hard-coding those breaks
 * pushes "Trusted Professionals," off the edge of a 320pt phone - the natural
 * break lands in the same place at every size that fits.
 */

export type OnboardingSlideKey = 'trust' | 'services' | 'booking';

export type OnboardingSlide = {
  key: OnboardingSlideKey;
  headline: string;
  body: string;
  /**
   * Full-panel illustration. The trust badges, the floating service icons and
   * the booking receipt are painted into these files - they are not live views
   * - so nothing should be layered on top of them.
   */
  image: ImageSourcePropType;
  /**
   * Width ÷ height of `image`.
   *
   * The three panels are cropped to their own content and so are different
   * shapes. Left to `contain`, each would be centred in the stage and they
   * would start at three different heights. Stating the ratio lets the screen
   * size each image to its true shape and sit them all on one baseline, so
   * only the top edge varies. Keep in step with the asset if it is recropped.
   */
  aspectRatio: number;
  /**
   * Points to raise this panel off the baseline. Optional; 0 when omitted.
   *
   * Panels are bottom-aligned so they share a baseline, which works when they
   * are similar shapes. These are not - slide 1 is square, 2 and 3 are around
   * a third wider than tall - so the wide pair sit visually lower in their
   * stage than the square one does. This lifts them back into agreement.
   */
  lift?: number;
  /**
   * Spoken description of the artwork. The text baked into each panel is
   * invisible to a screen reader, so the meaning is repeated here.
   */
  imageLabel: string;
};

export const onboardingSlides: OnboardingSlide[] = [
  {
    key: 'trust',
    headline: 'Trusted Professionals, All in One Place',
    body: 'Find verified experts for every home service you need.',
    /*
     * Supplied as `splash/onb1.png` on a 1536x1024 canvas with ~600px of
     * transparent padding across the width. Trimmed to its content and moved
     * here - untrimmed, `contain` fits the empty canvas and the artwork lands
     * far smaller than the stage it is given.
     */
    image: require('../../assets/uploads/onboarding/onb1.png'),
    aspectRatio: 926 / 934,
    imageLabel:
      'A Homingo professional in uniform carrying a toolbox, with badges reading ' +
      'verified professionals, background checked, and quality assured.',
  },
  {
    key: 'services',
    headline: 'Your home, on your schedule.',
    body: 'Choose a service, pick a time that suits you and book in just a few taps.',
    /* Trimmed from the supplied `onb2.png`, which had transparent padding down
       the left and across the top. */
    image: require('../../assets/uploads/onboarding/onb2.png'),
    aspectRatio: 1053 / 790,
    /* Higher than the others: this panel's artwork sits low inside its own
       bounds, so matching the numbers would not have matched the optics. */
    lift: 64,
    imageLabel:
      'A desk calendar with a date selected, a clock and a plant, above badges ' +
      'reading easy scheduling, flexible time slots and quick booking.',
  },
  {
    key: 'booking',
    headline: 'Book. Relax. We’ll Handle It.',
    body: 'Choose your time, sit back and relax. We’ll take care of the rest.',
    /*
     * Trimmed from the supplied `onb3.png`, which carried transparent padding
     * on all four sides.
     *
     * Unlike onb1 this artwork has an opaque warm-cream halo behind it
     * (~#EAE0D6, fading at the edges) rather than a clear background, so it
     * sits on a faintly warmer ground than the app canvas. That is the
     * artwork, not a bug - repainting it would mean touching the sofa and rug,
     * which are the same family of pale tones.
     */
    image: require('../../assets/uploads/onboarding/onb3.png'),
    aspectRatio: 1055 / 811,
    lift: 28,
    imageLabel:
      'A professional deep-cleaning a sofa, with a confirmed booking for deep ' +
      'cleaning on 16 May 2025 at 2 PM.',
  },
];
